import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PAYIN, PAYOUT, BASE_URLS } from "./ApiDocsData";

const METHOD_COLORS = {
  GET: "bg-emerald-100 text-emerald-700",
  POST: "bg-blue-100 text-blue-700",
  PATCH: "bg-amber-100 text-amber-700",
  PUT: "bg-orange-100 text-orange-700",
  DELETE: "bg-red-100 text-red-700",
};

function TableBlock({ title, headers, rows, colStyles = [] }) {
  return (
    <div>
      <p className="text-[11px] font-bold text-[#9B8FA0] uppercase mb-2">{title}</p>
      <div className="overflow-x-auto bg-[#0F172A] rounded-[10px]">
        <table className="w-full text-[12px]">
          <thead className="bg-[#1E293B] text-gray-300">
            <tr>
              {headers.map((h, i) => (
                <th key={i} className="px-4 py-2 text-left">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="text-gray-200">
            {rows.map((row, i) => (
              <tr key={i} className="border-t border-[#1E293B]">
                {row.map((cell, j) => (
                  <td key={j} className={`px-4 py-2 ${colStyles[j] || ""}`}>
                    {j === 2 && typeof cell === "boolean"
                      ? cell ? <span className="text-green-400">✔</span> : <span className="text-red-400">✖</span>
                      : cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EndpointBlock({ item }) {
  const [open, setOpen] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1800);
  };

  const CodeBlock = ({ label, content, ckey, color = "text-[#E2E8F0]" }) => (
    <div>
      <p className="text-[11px] font-bold text-[#9B8FA0] uppercase tracking-wider mb-1.5">{label}</p>
      <div className="relative bg-[#0F172A] rounded-[10px] overflow-hidden">
        <button
          onClick={() => copy(content, ckey)}
          className="absolute top-2.5 right-2.5 text-[10px] font-semibold text-[#9CA3AF] hover:text-white bg-[#1E293B] hover:bg-[#334155] px-2.5 py-1 rounded-md border-none cursor-pointer transition-all"
        >
          {copiedKey === ckey ? "Copied!" : "Copy"}
        </button>
        <pre className={`text-[12px] font-mono px-4 py-4 overflow-x-auto leading-relaxed ${color}`}>{content}</pre>
      </div>
    </div>
  );

  return (
    <div className="border border-[#F0EBF4] rounded-[14px] overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-5 py-4 bg-white hover:bg-[#FAF8FC] transition-colors border-none cursor-pointer text-left"
      >
        <span className={`text-[11px] font-bold px-2.5 py-1 rounded-md uppercase tracking-wide shrink-0 ${METHOD_COLORS[item.method] || "bg-gray-100 text-gray-600"}`}>
          {item.method}
        </span>
        <code className="text-[13px] font-mono text-[#4B2364] font-semibold flex-1 truncate">{item.path}</code>
        <span className="text-[13px] text-[#4C444E] hidden sm:block shrink-0">{item.title}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9B8FA0" strokeWidth="2.5"
          className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="px-5 pb-5 pt-3 bg-[#FDFCFE] border-t border-[#F0EBF4] space-y-4">
          <p className="text-[13px] text-[#4C444E]">{item.desc}</p>

          {item.headers && <CodeBlock label="Headers" content={item.headers} ckey="headers" color="text-[#FCA5A5]" />}
          {item.request && <CodeBlock label="Request Body" content={item.request} ckey="request" color="text-[#E2E8F0]" />}

          {item.queryParams && (
            <TableBlock
              title="Query Parameters"
              headers={["Param", "Description", "Default"]}
              rows={item.queryParams}
              colStyles={["font-mono text-purple-300", "", ""]}
            />
          )}

          {item.requestFields && (
            <TableBlock
              title="Request Fields"
              headers={["Field", "Type", "Required", "Description"]}
              rows={item.requestFields}
              colStyles={["font-mono text-purple-300", "", "", ""]}
            />
          )}

          {Array.isArray(item.response) ? (
            <TableBlock
              title={item.method === "TABLE" ? "Quick Reference" : "Error Reference"}
              headers={item.method === "TABLE"
                ? ["Method", "Endpoint", "Description"]
                : ["HTTP Code", "Message", "Reason"]}
              rows={item.response}
              colStyles={item.method === "TABLE"
                ? ["font-mono", "text-purple-300 font-mono", ""]
                : ["font-mono", "", ""]}
            />
          ) : (
            <CodeBlock label="Response" content={item.response} ckey="response" color="text-[#86EFAC]" />
          )}

          {item.responseFields && (
            <TableBlock
              title="Response Fields"
              headers={["Field", "Description"]}
              rows={item.responseFields}
              colStyles={["font-mono text-purple-300", ""]}
            />
          )}

          {item.callbackFields && (
            <TableBlock
              title="Callback Fields"
              headers={["Field", "Description"]}
              rows={item.callbackFields}
              colStyles={["font-mono text-purple-300", ""]}
            />
          )}

          {item.verification && (
            <CodeBlock label="Signature Verification" content={item.verification} ckey="verify" color="text-[#FCD34D]" />
          )}

          {item.integration && (
            <div>
              <p className="text-[14px] font-bold text-[#9B8FA0] uppercase mb-2">
                {item.integration.title}
              </p>
              {item.integration.options.map((opt, i) => (
                <div key={i} className="mb-4">
                  <p className="text-[15px] font-semibold text-[#4C444E] mb-2">{opt.name}</p>
                  <CodeBlock label="Example" content={opt.code} ckey={`integration-${i}`} color="text-[#E2E8F0]" />
                </div>
              ))}
            </div>
          )}

          {item.polling && (
            <CodeBlock label="Status Polling (Optional)" content={item.polling} ckey="polling" color="text-[#93C5FD]" />
          )}

          {item.verifySignature && (
            <CodeBlock label="Signature Verify (Security)" content={item.verifySignature} ckey="verifySignature" color="text-yellow-300" />
          )}
        </div>
      )}
    </div>
  );
}

export default function ApiDocs() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("payin");
  const [activeGroup, setActiveGroup] = useState("Overview");
  const endpoints = tab === "payin" ? PAYIN : PAYOUT;
  const current = endpoints.find(e => e.group === activeGroup) || endpoints[0];

  const handleTabSwitch = (t) => {
    setTab(t);
    setActiveGroup("Overview");
  };

  return (
    <div className="flex-1 overflow-y-auto bg-[#F8F5FB] px-8 py-7">
      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <button onClick={() => navigate("/api-key")}
            className="w-8 h-8 flex items-center justify-center rounded-[10px] bg-white shadow-sm border-none cursor-pointer hover:bg-[#F0EBF4] transition-colors">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4B2364" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <div>
            <p className="text-[12px] font-bold text-[#9B8FA0] uppercase tracking-wider">Developer Resources</p>
            <h1 className="text-[32px] font-bold text-[#34094D] leading-tight">API Documentation</h1>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-white rounded-[14px] p-1 shadow-[0px_4px_16px_0px_#4B23640A] w-fit mb-5">
          {[
            { key: "payin", label: "Payin", icon: "M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" },
            { key: "payout", label: "Payout", icon: "M5 12h14M12 5l7 7-7 7" },
          ].map(t => (
            <button key={t.key} onClick={() => handleTabSwitch(t.key)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-[10px] text-[14px] font-bold border-none cursor-pointer transition-all ${tab === t.key
                ? "bg-[#4B2364] text-white shadow-[0px_4px_12px_rgba(75,35,100,0.3)]"
                : "bg-transparent text-[#4C444E] hover:bg-[#F3F4F5]"
              }`}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d={t.icon} />
              </svg>
              {t.label}
            </button>
          ))}
        </div>

        {/* Base URL Banner */}
        <div className={`rounded-[16px] px-6 py-4 mb-6 flex items-center justify-between flex-wrap gap-3 ${tab === "payin" ? "bg-[#4B2364]" : "bg-[#065F46]"}`}>
          <div>
            <p className="text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1">Base URL — {tab === "payin" ? "Payin" : "Payout"}</p>
            <code className="text-[15px] font-mono font-bold text-white">{BASE_URLS[tab]}</code>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold text-white/60 uppercase tracking-wider">Auth:</span>
            <code className="text-[12px] font-mono text-white bg-white/10 px-3 py-1 rounded-lg">Authorization: Bearer YOUR_API_KEY</code>
          </div>
        </div>

        <div className="flex gap-6 items-start">

          {/* Sidebar */}
          <div className="w-48 shrink-0 bg-white rounded-[16px] shadow-[0px_4px_16px_0px_#4B23640A] p-3 sticky top-4">
            <p className="text-[10px] font-bold text-[#9B8FA0] uppercase tracking-wider px-2 mb-2">
              {tab === "payin" ? "Payin" : "Payout"} Groups
            </p>
            {endpoints.map(e => (
              <button key={e.group} onClick={() => setActiveGroup(e.group)}
                className={`w-full text-left px-3 py-2 rounded-[10px] text-[13px] font-semibold border-none cursor-pointer transition-colors mb-0.5 ${activeGroup === e.group
                  ? tab === "payin" ? "bg-[#4B2364]/10 text-[#4B2364]" : "bg-[#065F46]/10 text-[#065F46]"
                  : "bg-transparent text-[#4C444E] hover:bg-[#F3F4F5]"
                }`}>
                {e.group}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 space-y-3">
            <div className="flex items-center gap-2 mb-4">
              <span className={`w-2.5 h-2.5 rounded-full ${current.color}`} />
              <h2 className="text-[20px] font-bold text-[#34094D]">{current.group}</h2>
              <span className="text-[12px] text-[#9B8FA0] font-medium">
                {current.items.length} endpoint{current.items.length > 1 ? "s" : ""}
              </span>
            </div>
            {current.items.map(item => (
              <EndpointBlock key={item.path + item.method} item={item} />
            ))}
          </div>

        </div>
      </div>
    </div>
  );
}